/* ============================================================================
   Bro Glo — Bundle PDP JavaScript (v1.1)
   ============================================================================
   PDP JS for pre-made bundles. Gallery, shade selection, cross-sell checkbox,
   and ATC match the face tanner PDP patterns exactly.
   - variant-hidden class for gallery filtering (not inline styles)
   - translateX transforms for desktop gallery (not scrollTo)
   - data-slide attribute matching for dots/thumbs
   - BWCart.refresh for cart drawer
   - Cross-sell checkbox (not page navigation)
   ============================================================================ */

(function () {
  'use strict';

  const BrogloPdpBundle = {

    state: {},

    /* ── Init ── */
    init(sectionId) {
      const data = window.brogloPdpBundleData?.[sectionId];
      if (!data) return;

      const initialVariant = data.variants.find(v => v.id === data.currentVariantId);
      const initialShade = initialVariant?.handle || 'original';

      this.state[sectionId] = {
        data,
        currentVariantId: data.currentVariantId,
        currentShade: initialShade,
        currentSlide: 0,
        crossSellChecked: false
      };

      this.initGallery(sectionId);
      this.filterGalleryByVariant(sectionId, initialShade);
      this.initStickyAtc(sectionId);
      this.initUrgencyCycle(sectionId);
      this.initTickerCycle(sectionId);
      this.updatePricing(sectionId);

      /* URL ?variant= override */
      const urlVariantId = parseInt(new URLSearchParams(window.location.search).get('variant'));
      if (urlVariantId && urlVariantId !== data.currentVariantId) {
        const urlVariant = data.variants.find(v => v.id === urlVariantId);
        if (urlVariant) {
          const btn = document.querySelector(
            `#BrogloPdp-${sectionId} .broglo-pdp__shade-btn[data-shade="${urlVariant.handle}"]`
          );
          if (btn) this.selectShade(sectionId, btn);
        }
      }
    },

    /* ══════════════════════════════════════════════
       SHADE SELECTION (matches face tanner exactly)
       ══════════════════════════════════════════════ */

    selectShade(sectionId, btn) {
      const st = this.state[sectionId];
      if (!st) return;

      const shade = btn.dataset.shade;
      const variantId = parseInt(btn.dataset.variantId);

      st.currentShade = shade;
      st.currentVariantId = variantId;

      /* Active button */
      const container = document.getElementById(`brogloPdpShades-${sectionId}`);
      if (container) {
        container.querySelectorAll('.broglo-pdp__shade-btn').forEach(b => {
          b.classList.remove('active');
          b.setAttribute('aria-pressed', 'false');
        });
        btn.classList.add('active');
        btn.setAttribute('aria-pressed', 'true');
      }

      /* Section data-shade (CSS accent switching) */
      const section = document.getElementById(`BrogloPdp-${sectionId}`);
      if (section) section.dataset.shade = shade;

      /* Gallery badge */
      const badge = document.getElementById(`brogloPdpBadgeLeft-${sectionId}`);
      if (badge) badge.textContent = btn.querySelector('.broglo-pdp__shade-name')?.textContent || shade;

      /* Filter gallery */
      this.filterGalleryByVariant(sectionId, shade);

      /* Swap What's Inside images for shade */
      this.updateInsideImages(sectionId, shade);

      /* Update all pricing */
      this.updatePricing(sectionId);

      /* Sticky ATC */
      const variant = st.data.variants.find(v => v.id === variantId);
      const stickyName = document.getElementById(`brogloPdpStickyName-${sectionId}`);
      if (stickyName) stickyName.textContent = `${st.data.productTitle} — ${variant?.title || shade}`;

      /* URL */
      const url = new URL(window.location);
      url.searchParams.set('variant', variantId);
      history.replaceState(null, '', url);

      /* Announce */
      this.announce(sectionId, `${shade} shade selected`);
    },

    /* ══════════════════════════════════════════════
       PRICING (bundle price + optional cross-sell)
       ══════════════════════════════════════════════ */

    updatePricing(sectionId) {
      const st = this.state[sectionId];
      if (!st) return;

      const variant = st.data.variants.find(v => v.id === st.currentVariantId);
      if (!variant) return;

      const bundlePrice = variant.price;
      const comparePrice = variant.compareAtPrice || 0;
      const crossSellPrice = st.crossSellChecked ? (st.data.crossSell?.price || 0) : 0;
      const totalPrice = bundlePrice + crossSellPrice;

      const fmt = this.formatMoney;

      /* Main price */
      const priceEl = document.getElementById(`brogloPdpPrice-${sectionId}`);
      if (priceEl) priceEl.textContent = fmt(totalPrice);

      /* Compare-at */
      const compareEl = document.getElementById(`brogloPdpCompare-${sectionId}`);
      if (compareEl) {
        const totalCompare = comparePrice + crossSellPrice;
        if (comparePrice > bundlePrice) {
          compareEl.textContent = fmt(totalCompare);
          compareEl.classList.add('broglo-pdp__compare-price--visible');
          compareEl.style.display = '';
        } else {
          compareEl.style.display = 'none';
        }
      }

      /* Savings badge */
      const savingsEl = document.getElementById(`brogloPdpSavings-${sectionId}`);
      if (savingsEl) {
        if (comparePrice > bundlePrice) {
          savingsEl.textContent = `You save ${fmt(comparePrice - bundlePrice)}`;
          savingsEl.style.display = '';
        } else {
          savingsEl.style.display = 'none';
        }
      }

      /* ATC button */
      const atcBtn = document.getElementById(`brogloPdpAtcBtn-${sectionId}`);
      const atcPrice = document.getElementById(`brogloPdpAtcPrice-${sectionId}`);
      if (atcBtn) {
        atcBtn.disabled = !variant.available;
        if (variant.available && atcPrice) {
          atcPrice.textContent = ` \u2014 ${fmt(totalPrice)}`;
        }
      }

      /* Sticky price */
      const stickyPrice = document.getElementById(`brogloPdpStickyPrice-${sectionId}`);
      if (stickyPrice) stickyPrice.textContent = fmt(totalPrice);

      /* Shipping bar */
      this.updateShippingBar(sectionId, totalPrice);
    },

    updateShippingBar(sectionId, totalCents) {
      const threshold = this.state[sectionId]?.data?.freeShipThreshold;
      if (!threshold) return;
      const fill = document.getElementById(`brogloPdpShipFill-${sectionId}`);
      const text = document.getElementById(`brogloPdpShipText-${sectionId}`);
      if (!fill || !text) return;

      fill.style.width = Math.min(100, Math.round((totalCents / threshold) * 100)) + '%';
      const remaining = threshold - totalCents;
      text.innerHTML = remaining <= 0
        ? '\uD83C\uDF89 <strong style="color:#2D9B5E">You qualify for free shipping!</strong>'
        : `Add <strong>${this.formatMoney(remaining)}</strong> more for free shipping`;
    },

    /* ══════════════════════════════════════════════
       WHAT'S INSIDE — shade-aware image swap
       ══════════════════════════════════════════════ */

    updateInsideImages(sectionId, shade) {
      const section = document.getElementById(`BrogloPdp-${sectionId}`);
      if (!section) return;
      const imgs = section.querySelectorAll('.broglo-pdp__inside-img img[data-img-original]');
      imgs.forEach(img => {
        const src = shade === 'darker'
          ? (img.dataset.imgDarker || img.dataset.imgOriginal)
          : img.dataset.imgOriginal;
        if (src && img.src !== src) {
          img.style.opacity = '0';
          setTimeout(() => { img.src = src; img.style.opacity = '1'; }, 150);
        }
      });
    },

    /* ══════════════════════════════════════════════
       CROSS-SELL CHECKBOX
       ══════════════════════════════════════════════ */

    toggleCrossSell(sectionId) {
      const st = this.state[sectionId];
      if (!st) return;

      st.crossSellChecked = !st.crossSellChecked;

      const card = document.getElementById(`brogloPdpCrossSell-${sectionId}`);
      if (card) {
        card.classList.toggle('selected', st.crossSellChecked);
        card.setAttribute('aria-checked', st.crossSellChecked);
      }

      this.updatePricing(sectionId);
      this.announce(sectionId, st.crossSellChecked ? 'Added to order' : 'Removed from order');
    },

    /* ══════════════════════════════════════════════
       GALLERY (matches face tanner exactly)
       ══════════════════════════════════════════════ */

    initGallery(sectionId) {
      const slidesEl = document.getElementById(`brogloPdpSlides-${sectionId}`);
      if (!slidesEl) return;

      /* Mobile: scroll-snap detection */
      if (window.innerWidth < 768) {
        slidesEl.addEventListener('scroll', () => {
          const slides = slidesEl.querySelectorAll('.broglo-pdp__slide');
          const scrollLeft = slidesEl.scrollLeft;
          let closest = 0, minDist = Infinity;
          slides.forEach((slide, i) => {
            if (slide.classList.contains('variant-hidden')) return;
            const dist = Math.abs(slide.offsetLeft - scrollLeft - slidesEl.offsetWidth * 0.06);
            if (dist < minDist) { minDist = dist; closest = i; }
          });
          this.setSlide(sectionId, closest, false);
        }, { passive: true });
      }
    },

    setSlide(sectionId, index, scroll) {
      const st = this.state[sectionId];
      if (!st) return;
      st.currentSlide = index;

      /* Dots — match by data-slide */
      const dotsEl = document.getElementById(`brogloPdpDots-${sectionId}`);
      if (dotsEl) {
        dotsEl.querySelectorAll('.broglo-pdp__dot').forEach(dot => {
          dot.classList.toggle('active', parseInt(dot.dataset.slide) === index);
        });
      }

      /* Thumbs — match by data-slide */
      const thumbsEl = document.getElementById(`brogloPdpThumbs-${sectionId}`);
      if (thumbsEl) {
        thumbsEl.querySelectorAll('.broglo-pdp__thumb').forEach(thumb => {
          const isActive = parseInt(thumb.dataset.slide) === index;
          thumb.classList.toggle('active', isActive);
          if (isActive && !thumb.classList.contains('variant-hidden')) {
            const thumbLeft = thumb.offsetLeft - thumbsEl.offsetLeft;
            const thumbCenter = thumbLeft - (thumbsEl.offsetWidth / 2) + (thumb.offsetWidth / 2);
            thumbsEl.scrollTo({ left: thumbCenter, behavior: 'smooth' });
          }
        });
      }

      /* Desktop: translateX based on visible slides before target */
      if (scroll !== false && window.innerWidth >= 768) {
        const slidesEl = document.getElementById(`brogloPdpSlides-${sectionId}`);
        if (slidesEl) {
          const allSlides = Array.from(slidesEl.querySelectorAll('.broglo-pdp__slide'));
          let visibleOffset = 0;
          for (let i = 0; i < allSlides.length; i++) {
            if (i === index) break;
            if (!allSlides[i].classList.contains('variant-hidden')) visibleOffset++;
          }
          slidesEl.style.transform = `translateX(-${visibleOffset * 100}%)`;
        }
      }
    },

    goToSlide(sectionId, index) {
      this.setSlide(sectionId, index, true);

      /* Mobile: scroll into view */
      if (window.innerWidth < 768) {
        const slidesEl = document.getElementById(`brogloPdpSlides-${sectionId}`);
        const slide = slidesEl?.querySelectorAll('.broglo-pdp__slide')[index];
        if (slide && slidesEl) {
          slidesEl.scrollTo({ left: slide.offsetLeft - slidesEl.offsetLeft, behavior: 'smooth' });
        }
      }
    },

    filterGalleryByVariant(sectionId, shade) {
      const section = document.getElementById(`BrogloPdp-${sectionId}`);
      if (!section) return;

      const slides = section.querySelectorAll('.broglo-pdp__slide');
      const thumbs = section.querySelectorAll('.broglo-pdp__thumb');
      const dots = section.querySelectorAll('.broglo-pdp__dot');

      /* Check if any media has variant grouping */
      let hasVariantGroups = false;
      slides.forEach(s => {
        if (s.dataset.variantGroup && s.dataset.variantGroup !== 'shared') hasVariantGroups = true;
      });

      if (!hasVariantGroups) {
        slides.forEach(s => s.classList.remove('variant-hidden'));
        thumbs.forEach(t => t.classList.remove('variant-hidden'));
        dots.forEach(d => d.classList.remove('variant-hidden'));
        this.goToSlide(sectionId, 0);
        this.updateThumbArrows(sectionId);
        return;
      }

      let firstVisibleIndex = null;
      slides.forEach((slide, i) => {
        const group = slide.dataset.variantGroup || 'shared';
        const visible = (group === shade || group === 'shared');
        slide.classList.toggle('variant-hidden', !visible);
        if (visible && firstVisibleIndex === null) firstVisibleIndex = i;
      });

      thumbs.forEach(thumb => {
        const group = thumb.dataset.variantGroup || 'shared';
        thumb.classList.toggle('variant-hidden', !(group === shade || group === 'shared'));
      });

      dots.forEach(dot => {
        const group = dot.dataset.variantGroup || 'shared';
        dot.classList.toggle('variant-hidden', !(group === shade || group === 'shared'));
      });

      if (firstVisibleIndex !== null) this.goToSlide(sectionId, firstVisibleIndex);
      this.updateThumbArrows(sectionId);
    },

    scrollThumbs(sectionId, direction) {
      const container = document.getElementById(`brogloPdpThumbs-${sectionId}`);
      if (!container) return;
      container.scrollBy({ left: direction * 200, behavior: 'smooth' });
      setTimeout(() => this.updateThumbArrows(sectionId), 350);
    },

    updateThumbArrows(sectionId) {
      const thumbsEl = document.getElementById(`brogloPdpThumbs-${sectionId}`);
      const prevBtn = document.getElementById(`brogloPdpThumbPrev-${sectionId}`);
      const nextBtn = document.getElementById(`brogloPdpThumbNext-${sectionId}`);
      if (!thumbsEl) return;
      if (prevBtn) prevBtn.disabled = thumbsEl.scrollLeft <= 2;
      if (nextBtn) nextBtn.disabled = thumbsEl.scrollLeft + thumbsEl.clientWidth >= thumbsEl.scrollWidth - 2;
    },

    /* ══════════════════════════════════════════════
       ADD TO CART (bundle + optional cross-sell)
       ══════════════════════════════════════════════ */

    async addToCart(sectionId) {
      const st = this.state[sectionId];
      if (!st) return;

      const btn = document.getElementById(`brogloPdpAtcBtn-${sectionId}`);
      if (!btn || btn.disabled) return;

      const originalText = btn.innerHTML;
      btn.disabled = true;
      btn.innerHTML = '<span style="opacity:.7">Adding\u2026</span>';

      try {
        const items = [{ id: st.currentVariantId, quantity: 1 }];

        /* Include cross-sell if checked */
        if (st.crossSellChecked && st.data.crossSell?.variantId) {
          items.push({
            id: st.data.crossSell.variantId,
            quantity: 1,
            properties: { '_bundle_addon': 'true', '_discount_pct': String(st.data.crossSell.discountPct || 0) }
          });
        }

        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items })
        });

        if (!res.ok) throw new Error('Cart add failed');

        const cartData = await res.json();
        this.announce(sectionId, `${st.data.productTitle} added to cart`);

        btn.innerHTML = '\u2713 Added to Cart!';
        btn.style.background = '#2D9B5E';

        /* Update cart count */
        const count = cartData.items?.reduce?.((sum, i) => sum + i.quantity, 0);
        if (count) this.updateCartCount(count);

        /* Open cart drawer */
        setTimeout(() => this.openCartDrawer(), 300);

        setTimeout(() => {
          btn.innerHTML = originalText;
          btn.disabled = false;
          btn.style.background = '';
        }, 2000);

      } catch (err) {
        console.error('BrogloPdpBundle ATC error:', err);
        btn.innerHTML = 'Error \u2014 Try Again';
        btn.disabled = false;
        this.announce(sectionId, 'Error adding to cart. Please try again.');
        setTimeout(() => { btn.innerHTML = originalText; btn.style.background = ''; }, 2000);
      }
    },

    openCartDrawer() {
      /* Concept theme cart drawer */
      if (window.BWCart?.refresh) {
        BWCart.refresh(true);
        return;
      }
      /* Fallback: click cart icon */
      const headerCart = document.querySelector(
        '.header a[href="/cart"], header a[href*="/cart"], .header__icon--cart'
      );
      if (headerCart) { headerCart.click(); return; }
      window.location.href = '/cart';
    },

    updateCartCount(count) {
      document.querySelectorAll(
        '.cart-count-bubble span, [data-cart-count], .header__cart-count, .cart-count, .js-cart-count'
      ).forEach(el => { el.textContent = count; });
      document.querySelectorAll('.cart-count-bubble').forEach(el => {
        el.style.display = count > 0 ? '' : 'none';
      });
    },

    /* ══════════════════════════════════════════════
       STICKY ATC (matches face tanner)
       ══════════════════════════════════════════════ */

    initStickyAtc(sectionId) {
      const sticky = document.getElementById(`brogloPdpStickyAtc-${sectionId}`);
      const atcBtn = document.getElementById(`brogloPdpAtcBtn-${sectionId}`);
      if (!sticky || !atcBtn) return;

      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            sticky.classList.remove('visible');
          } else {
            /* Only show if ATC scrolled ABOVE viewport */
            sticky.classList.toggle('visible', entry.boundingClientRect.top < 0);
          }
        });
      }, { threshold: 0 });
      observer.observe(atcBtn);
    },

    /* ══════════════════════════════════════════════
       URGENCY COUNTER
       ══════════════════════════════════════════════ */

    initUrgencyCycle(sectionId) {
      const el = document.getElementById(`brogloPdpUrgency-${sectionId}`);
      if (!el) return;

      const cfg = this.state[sectionId]?.data?.urgency;
      if (!cfg) return;

      const storageKey = 'broglo_urgency_bundle_' + sectionId;
      const today = new Date().toDateString();
      let saved = null;
      try { saved = JSON.parse(localStorage.getItem(storageKey)); } catch (e) {}

      let count = (saved && saved.date === today && saved.count >= cfg.base)
        ? Math.min(saved.count, cfg.cap) : cfg.base;

      el.textContent = count + ' people bought this today';

      const scheduleNext = () => {
        const interval = cfg.minInterval + Math.random() * (cfg.maxInterval - cfg.minInterval);
        setTimeout(() => {
          const inc = cfg.minIncrement + Math.floor(Math.random() * (cfg.maxIncrement - cfg.minIncrement + 1));
          count = Math.min(count + inc, cfg.cap);
          el.textContent = count + ' people bought this today';
          try { localStorage.setItem(storageKey, JSON.stringify({ date: today, count })); } catch (e) {}
          if (count < cfg.cap) scheduleNext();
        }, interval * 1000);
      };
      if (count < cfg.cap) scheduleNext();
    },

    /* ══════════════════════════════════════════════
       TICKER (clean fade — was missing transition)
       ══════════════════════════════════════════════ */

    initTickerCycle(sectionId) {
      const el = document.getElementById(`brogloPdpTicker-${sectionId}`);
      if (!el) return;

      const msgs = this.state[sectionId]?.data?.tickerMessages?.filter(m => m && m.trim()) || [];
      if (msgs.length <= 1) return;

      let i = 0;
      el.style.transition = 'opacity 0.3s';

      setInterval(() => {
        i = (i + 1) % msgs.length;
        el.style.opacity = '0';
        setTimeout(() => { el.innerHTML = msgs[i]; el.style.opacity = '1'; }, 300);
      }, 4000);
    },

    /* ══════════════════════════════════════════════
       ACCORDION
       ══════════════════════════════════════════════ */

    toggleAccordion(trigger) {
      const item = trigger.closest('.broglo-pdp__accordion-item');
      if (!item) return;
      const isOpen = item.classList.contains('open');
      const content = item.querySelector('.broglo-pdp__accordion-content');
      if (!content) return;

      const accordion = item.closest('.broglo-pdp__accordion');
      if (accordion) {
        accordion.querySelectorAll('.broglo-pdp__accordion-item.open').forEach(other => {
          if (other !== item) {
            other.classList.remove('open');
            const c = other.querySelector('.broglo-pdp__accordion-content');
            const t = other.querySelector('.broglo-pdp__accordion-trigger');
            if (c) c.style.maxHeight = '0';
            if (t) t.setAttribute('aria-expanded', 'false');
          }
        });
      }

      if (isOpen) {
        item.classList.remove('open');
        content.style.maxHeight = '0';
        trigger.setAttribute('aria-expanded', 'false');
      } else {
        item.classList.add('open');
        content.style.maxHeight = content.scrollHeight + 'px';
        trigger.setAttribute('aria-expanded', 'true');
      }
    },

    /* ── Utilities ── */
    formatMoney(cents) {
      if (typeof cents !== 'number' || isNaN(cents)) return '$0.00';
      return '$' + (cents / 100).toFixed(2);
    },

    announce(sectionId, message) {
      const el = document.getElementById(`brogloPdpSrAnnounce-${sectionId}`);
      if (!el) return;
      el.textContent = '';
      requestAnimationFrame(() => { el.textContent = message; });
    }
  };

  window.BrogloPdpBundle = BrogloPdpBundle;

  /* Auto-init */
  const initAll = () => {
    document.querySelectorAll('.broglo-pdp[data-pdp-type="bundle"]').forEach(section => {
      const sectionId = section.dataset.sectionId;
      if (sectionId) BrogloPdpBundle.init(sectionId);
    });
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  /* Theme editor re-init */
  if (Shopify?.designMode) {
    document.addEventListener('shopify:section:load', (e) => {
      const section = e.target.querySelector('.broglo-pdp[data-pdp-type="bundle"]');
      if (section) BrogloPdpBundle.init(section.dataset.sectionId);
    });
  }

  /* Click delegation — scoped to bundle sections only */
  document.addEventListener('click', (e) => {
    const bundleSection = e.target.closest('.broglo-pdp[data-pdp-type="bundle"]');
    if (!bundleSection) return;
    const sectionId = bundleSection.dataset.sectionId;
    if (!sectionId) return;

    const dot = e.target.closest('.broglo-pdp__dot');
    const thumb = e.target.closest('.broglo-pdp__thumb');
    const shadeBtn = e.target.closest('.broglo-pdp__shade-btn');
    const xsellCard = e.target.closest('.broglo-pdp__xsell-card');

    if (dot) BrogloPdpBundle.goToSlide(sectionId, parseInt(dot.dataset.slide));
    if (thumb) BrogloPdpBundle.goToSlide(sectionId, parseInt(thumb.dataset.slide));
    if (shadeBtn && !shadeBtn.classList.contains('active')) BrogloPdpBundle.selectShade(sectionId, shadeBtn);
    if (xsellCard) BrogloPdpBundle.toggleCrossSell(sectionId);
  });

  /* Keyboard support for cross-sell checkbox (ADA: Enter/Space toggle) */
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;
    const xsellCard = e.target.closest('.broglo-pdp__xsell-card');
    if (!xsellCard) return;
    e.preventDefault();
    const sectionId = xsellCard.closest('.broglo-pdp[data-pdp-type="bundle"]')?.dataset.sectionId;
    if (sectionId) BrogloPdpBundle.toggleCrossSell(sectionId);
  });

})();
